import type { CSSProperties, FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { flowRunSteps } from '@/constants/product'

interface Geometry {
    track: CSSProperties
    progress: CSSProperties
    dot: CSSProperties
    ready: boolean
}

const EMPTY: Geometry = { track: {}, progress: {}, dot: {}, ready: false }

interface FlowTimelineProps {
    className?: string
}

/**
 * "How a flow runs": five steps on a line with a dot that travels from step
 * to step. Horizontal from tablet up, vertical on phones.
 */
export const FlowTimeline: FC<FlowTimelineProps> = ({ className }): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const nodeRefs = useRef<Array<HTMLSpanElement | null>>([])
    const inView = useInView(containerRef, { threshold: 0.35 })
    const reduced = useReducedMotion()
    const [active, setActive] = useState(0)
    const [instant, setInstant] = useState(false)
    const [geometry, setGeometry] = useState<Geometry>(EMPTY)
    const total = flowRunSteps.length

    const measure = useCallback(() => {
        const container = containerRef.current
        const nodes = nodeRefs.current

        if (!container || !nodes[0] || !nodes[total - 1]) return

        const box = container.getBoundingClientRect()
        const centre = (node: HTMLSpanElement | null) => {
            const rect = node?.getBoundingClientRect()

            return rect
                ? { x: rect.left - box.left + rect.width / 2, y: rect.top - box.top + rect.height / 2 }
                : { x: 0, y: 0 }
        }

        const first = centre(nodes[0])
        const last = centre(nodes[total - 1])
        const current = centre(nodes[active])
        const horizontal = Math.abs(last.y - first.y) < 4

        setGeometry({
            ready: true,
            track: horizontal
                ? { left: first.x, top: first.y - 1, width: last.x - first.x, height: 2 }
                : { left: first.x - 1, top: first.y, width: 2, height: last.y - first.y },
            progress: horizontal
                ? { left: first.x, top: first.y - 1, width: Math.max(0, current.x - first.x), height: 2 }
                : { left: first.x - 1, top: first.y, width: 2, height: Math.max(0, current.y - first.y) },
            dot: { transform: `translate3d(${current.x - 8}px, ${current.y - 8}px, 0)` }
        })
    }, [active, total])

    useEffect(() => {
        measure()

        const observer = new ResizeObserver(measure)

        if (containerRef.current) observer.observe(containerRef.current)

        return () => observer.disconnect()
    }, [measure])

    useEffect(() => {
        if (reduced || !inView) return

        const timer = window.setTimeout(() => {
            const next = (active + 1) % total

            setInstant(next === 0)
            setActive(next)
        }, active === total - 1 ? 3200 : 2300)

        return () => window.clearTimeout(timer)
    }, [active, inView, reduced, total])

    useEffect(() => {
        if (!instant) return

        const frame = requestAnimationFrame(() => setInstant(false))

        return () => cancelAnimationFrame(frame)
    }, [instant])

    const motion = instant || reduced ? 'none' : undefined

    return (
        <div ref={containerRef} className={cn('relative', className)}>
            <span aria-hidden='true' className='absolute rounded-full bg-ink/10' style={geometry.track} />

            <span
                aria-hidden='true'
                className='absolute rounded-full bg-ultramarine transition-[width,height] duration-1000 ease-calm'
                style={{ ...geometry.progress, transition: motion }}
            />

            <span
                aria-hidden='true'
                className={cn(
                    'absolute left-0 top-0 z-20 size-4 rounded-full bg-ultramarine shadow-[0_0_0_6px_rgba(47,55,255,0.16),0_6px_14px_-4px_rgba(47,55,255,0.7)] transition-transform duration-1000 ease-calm',
                    geometry.ready ? 'opacity-100' : 'opacity-0'
                )}
                style={{ ...geometry.dot, transition: motion }}
            />

            <ol className='relative grid gap-9 md:grid-cols-5 md:gap-6'>
                {flowRunSteps.map((step, index) => {
                    const reached = index <= active

                    return (
                        <li
                            key={step.id}
                            aria-current={index === active ? 'step' : undefined}
                            className='flex gap-5 md:flex-col md:items-center md:gap-0 md:text-center'
                        >
                            <span
                                ref={element => { nodeRefs.current[index] = element }}
                                className={cn(
                                    'relative z-10 grid size-10 shrink-0 place-items-center rounded-full border font-mono text-[11px] transition-colors duration-500',
                                    reached ? 'border-ultramarine/40 bg-ultramarine-soft text-ultramarine' : 'border-ink/15 bg-paper text-stone'
                                )}
                            >
                                {`0${index + 1}`}
                            </span>

                            <div className='md:mt-7'>
                                <h3 className={cn('font-heading text-lg font-semibold tracking-[-0.02em] transition-colors duration-500', reached ? 'text-ink' : 'text-ink/50')}>
                                    {step.label}
                                </h3>

                                <p className={cn('mt-1.5 text-[14px] leading-relaxed transition-colors duration-500 md:mx-auto md:max-w-[15rem]', index === active ? 'text-ink-soft' : 'text-stone')}>
                                    {step.text}
                                </p>
                            </div>
                        </li>
                    )
                })}
            </ol>
        </div>
    )
}
